import React, { useState } from "react";
import TableBase from "./Base";
import { EditOutlined } from "@ant-design/icons";
import { Space, Tag, Tooltip } from "antd";

import StartLocationForm from "../Forms/Route/StartLocation";
import EndLocationForm from "../Forms/Route/EndLocation";
import TimeShiftForm from "../Forms/Route/TimeShift";
import { getTranslation } from "../../utils/helper";
import RouteTimeSpeedForm from "../Forms/Route/Speed";
import BreakTimeShiftForm from "../Forms/Route/BreakTime";

const DriversRoutePlanTable = ({
  scrollHeight,
  actionWidth,
  withAction,
  handleUpdateDriver,
  reloadData,
  setting,
  ...props
}) => {
  const [timeShift, setTimeShift] = useState("");
  const [timeBreak, setTimeBreak] = useState("");
  const [startLocation, setStartLocation] = useState("");
  const [endLocation, setEndLocation] = useState("");
  const [speed, setSpeed] = useState("");

  const showTimeShiftForm = driverId => {
    setTimeShift(driverId);
  };

  const showTimeBreakForm = driverId => {
    setTimeBreak(driverId);
  };

  const showSpeedForm = driverId => {
    setSpeed(driverId);
  };

  const handleUpdateTimeShift = params => {
    setTimeShift("");
    setTimeBreak("");
    handleSubmit(params);
  };

  const handleUpdateSpeed = params => {
    setSpeed("");
    handleSubmit(params);
  };

  const showStartLocationForm = driverId => {
    setStartLocation(driverId);
  };

  const handleUpdateStartLocation = params => {
    setStartLocation("");
    handleSubmit(params);
  };

  const showEndLocationForm = driverId => {
    setEndLocation(driverId);
  };

  const handleUpdateEndLocation = params => {
    setEndLocation("");
    handleSubmit(params);
  };

  const handleSubmit = params => {
    handleUpdateDriver(params);
    setTimeout(() => {
      reloadData();
    }, 200);
  };

  const COLUMNS = [
    {
      title: getTranslation(setting, "Drivers", "Driver Name"),
      dataIndex: ["driver", "name"],
      width: 100,
      ellipsis: true,
      fixed: true,
      render(name, record) {
        return (
          <Tooltip title={name}>
            <span style={{ color: `#${record.driver.color}` }}>{name}</span>
          </Tooltip>
        );
      },
    },
    {
      title: getTranslation(setting, "Drivers", "Start Location"),
      dataIndex: "startAddress",
      width: 160,
      ellipsis: true,
      render(_, record) {
        const showForm = startLocation === record._id;
        return (
          <div className="editInlineForm" onClick={() => showStartLocationForm(record._id)}>
            {!showForm && (
              <Tooltip title={record.startAddress}>
                <div className="text">
                  {record.startAddress} <EditOutlined />
                </div>
              </Tooltip>
            )}
            {showForm && (
              <StartLocationForm
                driverId={record._id}
                address={record.startAddress}
                lat={record.startLat}
                lng={record.startLng}
                onSubmit={handleUpdateStartLocation}
              />
            )}
          </div>
        );
      },
    },
    {
      title: getTranslation(setting, "Drivers", "End Location"),
      dataIndex: "endAddress",
      width: 160,
      ellipsis: true,
      render(_, record) {
        const showForm = endLocation === record._id;
        return (
          <div className="editInlineForm" onClick={() => showEndLocationForm(record._id)}>
            {!showForm && (
              <Tooltip title={record.endAddress}>
                <div className="text">
                  {record.endAddress} <EditOutlined />
                </div>
              </Tooltip>
            )}
            {showForm && (
              <EndLocationForm
                driverId={record._id}
                address={record.endAddress}
                lat={record.endLat}
                lng={record.endLng}
                onSubmit={handleUpdateEndLocation}
              />
            )}
          </div>
        );
      },
    },
    {
      title: getTranslation(setting, "Drivers", "Shift Time"),
      dataIndex: "shiftTime",
      width: 140,
      ellipsis: true,
      render(_, record) {
        const showForm = timeShift === record._id;
        return (
          <div className="editInlineForm" onClick={() => showTimeShiftForm(record._id)}>
            {!showForm && (
              <div className="text">
                {record.shiftStart} - {record.shiftEnd} <EditOutlined />
              </div>
            )}
            {showForm && (
              <TimeShiftForm
                driverId={record._id}
                shiftStart={record.shiftStart ?? "00:00"}
                shiftEnd={record.shiftEnd ?? "23:59"}
                onSubmit={handleUpdateTimeShift}
              />
            )}
          </div>
        );
      },
    },
    {
      title: getTranslation(setting, "Drivers", "Break Time"),
      dataIndex: "breakTime",
      width: 140,
      ellipsis: true,
      render(_, record) {
        const showForm = timeBreak === record._id;
        return (
          <div className="editInlineForm" onClick={() => showTimeBreakForm(record._id)}>
            {!showForm && (
              <div className="text">
                {record.breakStart} - {record.breakEnd} <EditOutlined />
              </div>
            )}
            {showForm && (
              <BreakTimeShiftForm
                driverId={record._id}
                breakStart={record.breakStart}
                breakEnd={record.breakEnd}
                onSubmit={handleUpdateTimeShift}
              />
            )}
          </div>
        );
      },
    },
    {
      title: getTranslation(setting, "Drivers", "Speed"),
      dataIndex: "speed",
      width: 140,
      ellipsis: true,
      render(value, record) {
        const speeds = {
          X2SLOWER: getTranslation(setting, "Drivers", "Slow"),
          SLOWER: getTranslation(setting, "Drivers", "Slow"),
          SLOW: getTranslation(setting, "Drivers", "Slow"),
          NORMAL: getTranslation(setting, "Drivers", "Normal"),
          FAST: getTranslation(setting, "Drivers", "Fast"),
          FASTER: getTranslation(setting, "Drivers", "Fast"),
          X2FASTER: getTranslation(setting, "Drivers", "Fast"),
        };
        const showForm = speed === record._id;
        return (
          <div className="editInlineForm">
            {!showForm && (
              <div className="text" onClick={() => showSpeedForm(record._id)}>
                {speeds[value]} <EditOutlined />
              </div>
            )}
            {showForm && (
              <RouteTimeSpeedForm
                setting={setting}
                driverId={record._id}
                speed={record.speed ?? "NORMAL"}
                onSubmit={handleUpdateSpeed}
                onCancel={() => showSpeedForm("")}
              />
            )}
          </div>
        );
      },
    },
    {
      title: getTranslation(setting, "Drivers", "Capacity"),
      width: 100,
      ellipsis: true,
      render(record) {
        return record.driver.capacity || "";
      },
    },
    {
      title: getTranslation(setting, "Drivers", "Driver Skills"),
      dataIndex: ["driver", "skills"],
      width: 150,
      render(skills) {
        return (
          <Space size={[0, 8]} wrap>
            {skills?.map((skill, i) => (
              <Tag key={i}>{skill}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: getTranslation(setting, "Drivers", "Status"),
      dataIndex: "status",
      width: 100,
      ellipsis: true,
      fixed: "right",
      render(status) {
        return <span className={"status driverStatus " + status.toLowerCase()}>{status.toLowerCase()}</span>;
      },
    },
  ];

  if (withAction) {
    COLUMNS.push({
      key: "action",
      width: actionWidth || 100,
      fixed: "right",
      render(record) {
        return withAction(record);
      },
    });
  }

  if (scrollHeight) {
    return (
      <TableBase
        columns={COLUMNS}
        scroll={{ x: 991, y: scrollHeight }}
        rowKey={record => record.driver._id}
        {...props}
      />
    );
  } else {
    return <TableBase columns={COLUMNS} scroll={{ x: 991 }} rowKey={record => record.driver._id} {...props} />;
  }
};

export default DriversRoutePlanTable;
